import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  useColorModeValue
} from '@chakra-ui/react';
import {
  FiEye,
  FiThumbsUp,
  FiTrendingUp,
  FiClock
} from 'react-icons/fi';
// TopVideo interface is defined locally below
import CachedImage from './CachedImage';
import IconWrapper from './IconWrapper';

interface TopVideo {
  id: string;
  title: string;
  thumbnail: string;
  views: number;
  likes: number;
  duration: string;
  uploadDate: Date;
  engagementRate: number;
}

interface TopVideosProps {
  videos: TopVideo[];
  isLoading?: boolean;
}

const TopVideos: React.FC<TopVideosProps> = ({ videos, isLoading = false }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (isLoading) {
    return (
      <VStack align="stretch" gap={{ base: 2, md: 3 }}>
        {[...Array(3)].map((_, index) => (
          <Box key={index} p={{ base: 3, md: 4 }} bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
            <HStack gap={{ base: 2, md: 3 }}>
              <Box w={{ base: "50px", md: "60px" }} h={{ base: "28px", md: "34px" }} bg="gray.200" borderRadius="md" />
              <VStack align="start" flex="1">
                <Box h={{ base: 3, md: 4 }} w="60%" bg="gray.200" borderRadius="md" />
                <Box h={{ base: 2, md: 3 }} w="40%" bg="gray.200" borderRadius="md" />
              </VStack>
            </HStack>
          </Box>
        ))}
      </VStack>
    );
  }

  return (
    <VStack align="stretch" gap={{ base: 2, md: 3 }}>
      {videos.map((video, index) => (
        <Box
          key={video.id}
          p={{ base: 3, md: 4 }}
          bg={cardBg}
          borderRadius="md"
          border="1px solid"
          borderColor={borderColor}
          _hover={{ shadow: 'md' }}
          transition="all 0.2s"
        >
          <HStack gap={{ base: 2, md: 3 }}>
            {/* Rank and Thumbnail */}
            <VStack align="center" gap={1}>
              <Badge
                colorScheme={index === 0 ? 'gold' : index === 1 ? 'gray' : index === 2 ? 'orange' : 'gray'}
                variant="solid"
                fontSize={{ base: '2xs', md: 'xs' }}
                px={{ base: 1, md: 2 }}
                py={{ base: 0.5, md: 1 }}
              >
                #{index + 1}
              </Badge>
              {video.thumbnail ? (
                <CachedImage
                  src={video.thumbnail}
                  alt={video.title}
                  width="60px"
                  height="34px"
                  borderRadius="md"
                  objectFit="cover"
                  fallbackType="video"
                />
              ) : (
                <Box
                  w={{ base: "50px", md: "60px" }}
                  h={{ base: "28px", md: "34px" }}
                  bg="gray.200"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <IconWrapper icon={FiEye} size={16} color="gray.400" />
                </Box>
              )}
            </VStack>

            {/* Video Info */}
            <VStack align="start" flex="1" gap={1}>
              <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="bold" noOfLines={2}>
                {video.title}
              </Text>
              <HStack gap={2}>
                <HStack gap={1}>
                  <IconWrapper icon={FiClock} size={12} color={textColor} />
                  <Text fontSize={{ base: '2xs', md: 'xs' }} color={textColor}>
                    {video.duration}
                  </Text>
                </HStack>
                <Text fontSize={{ base: '2xs', md: 'xs' }} color={textColor}>•</Text>
                <Text fontSize={{ base: '2xs', md: 'xs' }} color={textColor}>
                  {video.uploadDate.toLocaleDateString()}
                </Text>
              </HStack>
            </VStack>

            {/* Performance Metrics */}
            <VStack align="end" gap={1} display={{ base: 'none', sm: 'flex' }}>
              <HStack gap={{ base: 2, md: 3 }}>
                <VStack gap={0} align="center">
                  <IconWrapper icon={FiEye} size={16} color="blue.500" />
                  <Text fontSize={{ base: '2xs', md: 'xs' }} fontWeight="bold" color="blue.500">
                    {video.views.toLocaleString()}
                  </Text>
                </VStack>
                <VStack gap={0} align="center">
                  <IconWrapper icon={FiThumbsUp} size={16} color="red.500" />
                  <Text fontSize={{ base: '2xs', md: 'xs' }} fontWeight="bold" color="red.500">
                    {video.likes.toLocaleString()}
                  </Text>
                </VStack>
              </HStack>
              
              <HStack gap={2}>
                <Badge
                  colorScheme="green"
                  variant="subtle"
                  fontSize={{ base: '2xs', md: 'xs' }}
                  px={{ base: 1, md: 2 }}
                  py={{ base: 0.5, md: 1 }}
                >
                  <IconWrapper icon={FiTrendingUp} size={12} />
                  {video.engagementRate.toFixed(1)}%
                </Badge>
              </HStack>
            </VStack>
          </HStack>
        </Box>
      ))}
    </VStack>
  );
};

export default TopVideos;
